import { Folder, TrashItem } from '../types';
import { SecretCollectionPin } from './secretCollections';

export const BACKUP_FORMAT = 'minderbox-backup';
export const LEGACY_BACKUP_FORMAT = 'easy-done-backup';
export const BACKUP_VERSION = 1;
type BackupFormat = typeof BACKUP_FORMAT | typeof LEGACY_BACKUP_FORMAT;

export interface BackupData {
    folders: Folder[];
    selectedCollectionId: string | null;
    trash: TrashItem[];
    secretCollectionPin: SecretCollectionPin | null;
}

export interface AppBackup {
    format: BackupFormat;
    version: typeof BACKUP_VERSION;
    exportedAt: string;
    data: BackupData;
}

const isSecretCollectionPin = (value: unknown): value is SecretCollectionPin => {
    if (!value || typeof value !== 'object') return false;

    const pin = value as Record<string, unknown>;
    return typeof pin.salt === 'string' && typeof pin.hash === 'string';
};

export const createBackup = (data: BackupData): AppBackup => ({
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    data,
});

export const parseBackup = (contents: string): AppBackup | null => {
    try {
        const backup = JSON.parse(contents) as Partial<AppBackup>;
        const data = backup.data as Partial<BackupData> | undefined;

        if (
            (backup.format !== BACKUP_FORMAT && backup.format !== LEGACY_BACKUP_FORMAT)
            || backup.version !== BACKUP_VERSION
            || !data
            || !Array.isArray(data.folders)
            || !Array.isArray(data.trash)
            || (data.selectedCollectionId !== null && typeof data.selectedCollectionId !== 'string')
            || (data.secretCollectionPin !== null && !isSecretCollectionPin(data.secretCollectionPin))
        ) {
            return null;
        }

        return backup as AppBackup;
    } catch {
        return null;
    }
};

const createBackupFilename = (prefix: string): string => {
    const date = new Date();
    const formatPart = (value: number): string => value.toString().padStart(2, '0');
    const timestamp = [
        formatPart(date.getDate()),
        formatPart(date.getMonth() + 1),
        date.getFullYear(),
    ].join('-');
    const time = [
        formatPart(date.getHours()),
        formatPart(date.getMinutes()),
        formatPart(date.getSeconds()),
    ].join('-');

    return `minderbox-${prefix}-${timestamp}_${time}.json`;
};

export const downloadBackup = (backup: AppBackup, prefix = 'backup'): string => {
    const filename = createBackupFilename(prefix);
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    return filename;
};
